
import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { formatStorageSize } from './utils/databaseLimits';

export default function DatabaseLimitModal({ 
  show, 
  onClose, 
  onUpgrade, 
  storageInfo,
  canProceed = false,
  onProceedAnyway = null 
}) {
  const { t } = useTranslation();

  if (!show) return null;

  const usagePercentage = (storageInfo.currentUsage / storageInfo.limit) * 100;

  return (
    <Modal show={show} onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('databaseLimit.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('databaseLimit.currentUsage')}: {formatStorageSize(storageInfo.currentUsage)} / {formatStorageSize(storageInfo.limit)}
            </p>
          </div>
        </div>

        {/* Storage usage bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                usagePercentage >= 95 ? 'bg-red-500' : 
                usagePercentage >= 80 ? 'bg-orange-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {usagePercentage.toFixed(1)}% {t('databaseLimit.used')}
          </p>
        </div>

        <div className="space-y-4">
          <p className="text-gray-700">
            {t('databaseLimit.description')}
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">{t('databaseLimit.suggestions')}</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• {t('databaseLimit.deleteOldItems')}</li>
              <li>• {t('databaseLimit.removeImages')}</li>
              <li>• {t('databaseLimit.upgradeForMore')}</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          {canProceed && onProceedAnyway && (
            <button
              onClick={onProceedAnyway}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('databaseLimit.proceedWithoutImages')}
            </button>
          )}
          
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          
          <button
            onClick={onUpgrade}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('premium.upgrade')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
